/**
 * LessonStudyPanel — sidebar/section for a lesson viewer that surfaces:
 *   1. A "live-now" banner if the instructor is currently hosting a session.
 *   2. Instructor availability card (online/away/offline dot, Message,
 *      "Next live session" link).
 *   3. Active peer-created study rooms for this lesson/course.
 *   4. A "Study together" CTA that spins up a new study room in one click.
 *
 * Backed by:
 *   GET  /api/lms/lessons/{lesson_id}/study-context
 *   POST /api/live-sessions   (session_type = "study_room")
 *   POST /api/dm/conversations (start/get DM with instructor)
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Video, Users, MessageCircle, CircleDot, CalendarClock, Sparkles,
  Loader2, ArrowRight, Presentation,
} from 'lucide-react';
import { formatDistanceToNow, parseISO, format } from 'date-fns';

const STATUS_DOT = {
  online:  'bg-emerald-500',
  offline: 'bg-slate-400',
};

const LessonStudyPanel = ({ lessonId, lessonTitle }) => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [messaging, setMessaging] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', when: 'now', scheduled: '', duration: 60, max_seats: 8,
  });

  const fetchCtx = useCallback(async () => {
    if (!lessonId) return;
    try {
      const { data } = await axios.get(`${API}/lms/lessons/${lessonId}/study-context`);
      setCtx(data);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, [API, lessonId]);

  useEffect(() => { fetchCtx(); }, [fetchCtx]);

  // Prefill dialog when it opens.
  useEffect(() => {
    if (!createOpen) return;
    setForm((f) => ({
      ...f,
      title: f.title || `Study: ${(lessonTitle || 'Lesson').slice(0, 60)}`,
      description: f.description || `Peer study room for the lesson "${lessonTitle || ''}".`,
      scheduled: f.scheduled || new Date(Date.now() + 15 * 60 * 1000).toISOString().slice(0, 16),
    }));
  }, [createOpen, lessonTitle]);

  const openCreateDialog = () => setCreateOpen(true);

  const startStudyRoom = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const scheduledIso = form.when === 'now'
        ? new Date().toISOString()
        : new Date(form.scheduled).toISOString();
      const { data } = await axios.post(`${API}/live-sessions`, {
        title:            (form.title || `Study: ${lessonTitle || 'Lesson'}`).slice(0, 80),
        description:      form.description || '',
        scheduled_start:  scheduledIso,
        duration_minutes: Math.max(15, Math.min(240, parseInt(form.duration, 10) || 60)),
        max_attendees:    Math.max(2, Math.min(50, parseInt(form.max_seats, 10) || 8)),
        session_type:     'study_room',
        lesson_id:        lessonId,
        course_id:        ctx?.course_id || null,
        features:         { whiteboard: true, breakouts: true, chat: true, screenshare: true },
      });
      toast.success(form.when === 'now' ? 'Study room live — sharing the link' : 'Study room scheduled');
      setCreateOpen(false);
      if (form.when === 'now') navigate(`/live/${data.id}/room`);
      else navigate(`/live/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create study room');
    } finally { setCreating(false); }
  };

  const messageInstructor = async () => {
    if (!ctx?.instructor?.id || ctx.instructor.id === user?.id || messaging) return;
    setMessaging(true);
    try {
      const { data } = await axios.post(`${API}/dm/conversations`, {
        peer_id: ctx.instructor.id,
        initial_message: `Hi ${ctx.instructor.name.split(' ')[0]}, I have a question about "${lessonTitle || 'this lesson'}".`,
      });
      const convId = data?.conversation?.id;
      if (convId) navigate(`/messages/${convId}`);
      else navigate('/messages');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open DM');
      setMessaging(false);
    }
  };

  if (!lessonId) return null;

  return (
    <div className="space-y-3" data-testid="lesson-study-panel">
      {/* Live-now banner */}
      {ctx?.instructor_live_now && (
        <div
          role="button"
          onClick={() => navigate(`/live/${ctx.instructor_live_now.id}/room`)}
          className="cursor-pointer rounded-2xl border border-rose-400/50 bg-gradient-to-r from-rose-500/10 via-rose-500/5 to-transparent p-3 flex items-center gap-3 hover:border-rose-400 transition-colors"
          data-testid="instructor-live-now-banner"
        >
          <div className="h-9 w-9 rounded-full bg-rose-500 text-white flex items-center justify-center flex-shrink-0 animate-pulse">
            <CircleDot className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-rose-600 dark:text-rose-300">
              Live now · Instructor is hosting
            </div>
            <div className="font-heading text-sm font-extrabold truncate">
              {ctx.instructor_live_now.title}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-rose-600" />
        </div>
      )}

      {/* Instructor availability */}
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="instructor-card">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
          Instructor
        </div>
        {loading ? (
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> loading…
          </div>
        ) : ctx?.instructor ? (
          <>
            <div className="flex items-center gap-3">
              <div className="relative flex-shrink-0">
                <div className="h-11 w-11 rounded-full bg-primary/15 text-primary flex items-center justify-center text-sm font-bold overflow-hidden">
                  {ctx.instructor.avatar_url
                    ? <img src={ctx.instructor.avatar_url} alt="" className="h-full w-full object-cover" />
                    : (ctx.instructor.name || '?').slice(0, 2).toUpperCase()}
                </div>
                <span
                  className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-card ${STATUS_DOT[ctx.instructor.is_online ? 'online' : 'offline']}`}
                  data-testid={`instructor-status-${ctx.instructor.is_online ? 'online' : 'offline'}`}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading text-sm font-extrabold truncate">
                  {ctx.instructor.name}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {ctx.instructor.is_online
                    ? 'Online now — usually replies within an hour'
                    : ctx.instructor.last_active_at
                      ? `Away · last seen ${formatDistanceToNow(parseISO(ctx.instructor.last_active_at), { addSuffix: true })}`
                      : 'Away · will reply when back'}
                </div>
              </div>
            </div>

            {ctx.instructor.next_live_session && (
              <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-2.5 flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Next live session</div>
                  <div className="text-xs font-semibold truncate">{ctx.instructor.next_live_session.title}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {(() => {
                      try {
                        const d = parseISO(ctx.instructor.next_live_session.scheduled_start);
                        return `${format(d, 'EEE MMM d · h:mm a')} · ${formatDistanceToNow(d, { addSuffix: true })}`;
                      } catch (_) { return ''; }
                    })()}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-full h-7 text-[11px] font-bold flex-shrink-0"
                  onClick={() => navigate(`/live/${ctx.instructor.next_live_session.id}`)}
                  data-testid="next-live-session-btn"
                >
                  View
                </Button>
              </div>
            )}

            {ctx.instructor.id && ctx.instructor.id !== user?.id && (
              <Button
                onClick={messageInstructor}
                disabled={messaging}
                variant="outline"
                className="mt-3 w-full rounded-full font-bold"
                data-testid="message-instructor-btn"
              >
                {messaging
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> opening…</>
                  : <><MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Message instructor</>}
              </Button>
            )}
          </>
        ) : (
          <div className="text-xs text-muted-foreground">
            Instructor info not available for this lesson.
          </div>
        )}
      </div>

      {/* Study together */}
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="study-together-card">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
              Study together
            </div>
            <div className="font-heading text-sm font-extrabold">
              {ctx?.active_study_rooms?.length > 0
                ? `${ctx.active_study_rooms.length} room${ctx.active_study_rooms.length === 1 ? '' : 's'} open now`
                : 'No one studying yet'}
            </div>
          </div>
          <Sparkles className="w-4 h-4 text-primary" />
        </div>

        {ctx?.active_study_rooms?.length > 0 && (
          <ul className="space-y-1.5 mb-3">
            {ctx.active_study_rooms.slice(0, 4).map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/live/${r.id}/room`)}
                  className="w-full flex items-center gap-2 rounded-lg border border-border bg-background hover:bg-muted/50 px-2.5 py-2 text-left"
                  data-testid={`study-room-item-${r.id}`}
                >
                  <div className="h-7 w-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <Users className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold truncate">{r.title}</div>
                    <div className="text-[10px] text-muted-foreground">
                      Hosted by {r.host_name?.split('@')[0]} · {r.live_attendee_count || 0} in room
                    </div>
                  </div>
                  {r.status === 'live' && (
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white flex-shrink-0">Live</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        <Button
          onClick={openCreateDialog}
          disabled={creating}
          className="w-full rounded-full font-bold"
          data-testid="start-study-room-btn"
        >
          {creating
            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> creating…</>
            : <><Video className="w-3.5 h-3.5 mr-1.5" /> Start a study group</>}
        </Button>
        <p className="text-[10px] text-muted-foreground mt-2 leading-snug">
          Study rooms include collaborative whiteboard, breakout rooms and chat — powered by Jitsi Meet.
        </p>
      </div>

      {/* Create Study Group dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-study-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-extrabold inline-flex items-center gap-2">
              <Presentation className="w-5 h-5 text-primary" /> Start a study group
            </DialogTitle>
            <DialogDescription>
              Spin up a peer-hosted room with whiteboard, breakout rooms and shared notes for this lesson.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Room name</label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Study: Batch prep pitfalls"
                maxLength={80}
                data-testid="study-group-title"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Description · optional</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
                placeholder="What do you want to work on together?"
                data-testid="study-group-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">When</label>
                <div className="flex rounded-full border border-input p-0.5 mt-1">
                  {['now', 'later'].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, when: v }))}
                      data-testid={`study-group-when-${v}`}
                      className={`flex-1 h-8 rounded-full text-xs font-bold transition-colors ${
                        form.when === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {v === 'now' ? 'Start now' : 'Schedule'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Max seats</label>
                <Input
                  type="number"
                  min="2"
                  max="50"
                  value={form.max_seats}
                  onChange={(e) => setForm((f) => ({ ...f, max_seats: e.target.value }))}
                  data-testid="study-group-max-seats"
                />
              </div>
            </div>
            {form.when === 'later' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Start time</label>
                  <Input
                    type="datetime-local"
                    value={form.scheduled}
                    onChange={(e) => setForm((f) => ({ ...f, scheduled: e.target.value }))}
                    data-testid="study-group-scheduled"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Duration (min)</label>
                  <Input
                    type="number"
                    min="15"
                    max="240"
                    value={form.duration}
                    onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
                    data-testid="study-group-duration"
                  />
                </div>
              </div>
            )}
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-[11px] text-primary flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Whiteboard-first:</strong> your room opens with the Jitsi whiteboard tool ready for diagrams,
                sticky notes, and drawing.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
              className="rounded-full font-bold"
            >
              Cancel
            </Button>
            <Button
              onClick={startStudyRoom}
              disabled={creating || !form.title.trim() || (form.when === 'later' && !form.scheduled)}
              className="rounded-full font-bold"
              data-testid="study-group-create-submit"
            >
              {creating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> creating…</> : form.when === 'now' ? 'Start now' : 'Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LessonStudyPanel;
